/**
 * 実績トレンド計画反映 確認ダイアログ。
 *
 * - 適用対象コマ数・補正前後合計値・乖離率バッジを表示
 * - 「反映する」で onApply を呼ぶ
 * - 「キャンセル」で onCancel を呼ぶ
 */
import { useTranslation } from 'react-i18next'

import { Flex, Text } from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { DeviationBadge } from '~/pages/DemandForecastDaily/components/DeviationBadge'

type Props = {
  /** ダイアログの開閉状態 */
  isOpen: boolean
  /** 乖離率（avg_ratio: 1.0 基準） */
  avgRatio: number | null
  /** 集計窓 W (コマ数, demand-trend-window-variable design)。表示専用 (readOnly)。 */
  windowKoma: number
  /** 補正対象コマ数 */
  applicableKomaCount: number
  /** 補正前合計 (kWh) */
  beforeTotalKwh: number
  /** 補正後合計 (kWh) */
  afterTotalKwh: number
  /** 「反映する」クリック */
  onApply: () => void
  /** 「キャンセル」クリック */
  onCancel: () => void
  /**
   * live-basis の再計算 (PreviewTrendReflection) が発火中かどうか
   * (demand-trend-reflection-single-authority design D5)。「反映する」ボタンを
   * loading 表示 + disabled にする。
   */
  isApplyPending?: boolean
  /**
   * live-basis の再計算応答が未到着かどうか。true の間は「反映する」を
   * disabled にする (backend 系列が届く前に古い/空の値で Apply させない)。
   */
  isApplyDisabled?: boolean
}

/**
 * 実績トレンドをコマに反映する前の確認ダイアログ。
 */
export const TrendReflectDialog = ({
  isOpen,
  avgRatio,
  windowKoma,
  applicableKomaCount,
  beforeTotalKwh,
  afterTotalKwh,
  onApply,
  onCancel,
  isApplyPending = false,
  isApplyDisabled = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const formatKwh = (v: number): string =>
    `${Math.round(v).toLocaleString('ja-JP')} kWh`

  const footer = (
    <SaveCancelFooter
      isPending={isApplyPending}
      isSaveDisabled={isApplyDisabled}
      onCancel={onCancel}
      onSave={onApply}
      saveLabel={t('page:demandForecastDaily.trendReflect.dialogApply')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onCancel}
      // 幅は UI 辞書 components.md `.ecl-modal` の 4 段 (既定 md)。 8080 に対応する画面が無いので既定の md
      size="md"
      title={t('page:demandForecastDaily.trendReflect.dialogTitle')}
    >
      <Flex flexDirection="column" gap={3}>
        <Text fontSize="sm">
          {t('page:demandForecastDaily.trendReflect.dialogDescription', {
            count: applicableKomaCount,
          })}
        </Text>
        <Flex alignItems="center" gap={2}>
          <Text fontSize="sm">
            {t('page:demandForecastDaily.trendReflect.deviationLabel')}
          </Text>
          {/* AC-30: avgRatio 算出不可 (実績コマ2件未満) の場合はこのダイアログ自体が
              呼び出し元 (hasTrendReflect gate) で表示されない想定だが、
              DeviationBadge (AC-28) は non-null 契約のため念のため防御する */}
          {avgRatio !== null && (
            <DeviationBadge
              avgRatio={avgRatio}
              readOnly
              windowKoma={windowKoma}
            />
          )}
        </Flex>
        <Flex flexDirection="column" gap={1}>
          <Text fontSize="sm">
            {t('page:demandForecastDaily.trendReflect.beforeTotal')}:{' '}
            {formatKwh(beforeTotalKwh)}
          </Text>
          <Text fontSize="sm">
            {t('page:demandForecastDaily.trendReflect.afterTotal')}:{' '}
            {formatKwh(afterTotalKwh)}
          </Text>
        </Flex>
      </Flex>
    </Modal>
  )
}
